import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { websiteApi } from '../api/website.api';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import UserMenu from '../components/UserMenu';
import { getPreviewUrl, getLiveUrl, shareWebsite } from '../utils/websiteUrls';
import { useNotify } from '../context/NotifyContext';
import usePageMeta from '../hooks/usePageMeta';

type GenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';

interface WebsiteGeneration {
  status?: GenerationStatus;
  totalSections?: number;
  completedSections?: number;
  currentStep?: string | null;
  error?: string;
}

interface Website {
  _id: string;
  businessName: string;
  slug: string;
  status: 'draft' | 'published' | 'unpublished';
  hasPublishedVersion?: boolean;
  createdAt: string;
  logo?: string;
  generation?: WebsiteGeneration;
}

interface GenerationProgressEvent {
  websiteId: string;
  generation?: WebsiteGeneration;
}

const GENERATION_EVENT = 'website:generation-progress';

const DashboardPage: React.FC = () => {
  usePageMeta({ title: 'Dashboard — OnTheWeb', index: false });

  const { notify, confirm } = useNotify();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const { socket, isConnected } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadWebsites();
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(''), 8000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const isGenerating = (w: Website) => {
    const status = w.generation?.status;
    return status === 'pending' || status === 'generating';
  };

  // Live progress: the server pushes an update over the socket every time a
  // website's generation status changes, so no polling is needed while the
  // dashboard is open. A full refetch on (re)connect reconciles state in
  // case any events were missed while disconnected (e.g. brief network
  // drop), and also picks up brand-new websites created in another tab.
  useEffect(() => {
    if (!socket) return;

    const handleProgress = (event: GenerationProgressEvent) => {
      setWebsites((prev) => {
        const exists = prev.some((w) => w._id === event.websiteId);
        if (!exists) {
          // Unknown website (e.g. just created in another tab) — refetch the full list.
          loadWebsites({ silent: true });
          return prev;
        }
        return prev.map((w) =>
          w._id === event.websiteId
            ? {
                ...w,
                generation: { ...w.generation, ...event.generation },
              }
            : w
        );
      });
    };

    socket.on(GENERATION_EVENT, handleProgress);
    socket.on('connect', () => loadWebsites({ silent: true }));

    return () => {
      socket.off(GENERATION_EVENT, handleProgress);
      socket.off('connect');
    };
  }, [socket]);

  const loadWebsites = async (options?: { silent?: boolean }) => {
    try {
      const data = await websiteApi.getMyWebsites();
      setWebsites(data);
    } catch (error) {
      console.error('Failed to load websites:', error);
    } finally {
      if (!options?.silent) setLoading(false);
    }
  };

  const handleDeleteWebsite = async (websiteId: string) => {
    const ok = await confirm({
      title: 'Delete website',
      message: 'Are you sure you want to delete this website? This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await websiteApi.deleteWebsite(websiteId);
      setWebsites(websites.filter((w) => w._id !== websiteId));
    } catch (error) {
      console.error('Failed to delete website:', error);
      notify.error('Failed to delete website');
    }
  };

  const handleShare = async (website: Website) => {
    try {
      const result = await shareWebsite({
        businessName: website.businessName,
        slug: website.slug,
        hasPublishedVersion: website.hasPublishedVersion,
      });
      if (result === 'copied') {
        notify.success('Link copied to clipboard.');
      }
    } catch (error) {
      console.error('Failed to share website:', error);
      notify.error('Failed to share website');
    }
  };

  return (
    <div className="nova-page overflow-x-hidden">
      <nav className="nova-nav relative border-b border-nova-border">
        <div className="nova-nav-inner">
          <Logo />
          <div className="flex items-center gap-3 sm:gap-4">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </nav>

      <div className="max-w-content mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-10 pt-20 sm:pt-24 min-w-0 overflow-x-hidden">
        {successMessage && (
          <div className="nova-alert-success mb-6 animate-fade-in-up">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage('')}
              className="text-nova-green/70 hover:text-nova-green self-end sm:self-auto p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px]"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8">
          <div>
            <div className="section-tag">Dashboard</div>
            <h2 className="font-display text-3xl font-extrabold text-nova-text">My Websites</h2>
          </div>
          <button onClick={() => navigate('/create-website')} className="btn-primary w-full sm:w-auto justify-center">
            + Create New Website
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-nova-sky"></div>
            <p className="mt-4 text-nova-text2">Loading your websites...</p>
          </div>
        ) : websites.length === 0 ? (
          <div className="nova-card text-center py-16">
            <svg className="mx-auto h-20 w-20 text-nova-text3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-4 font-display text-xl font-bold text-nova-text">No websites yet</h3>
            <p className="mt-2 text-nova-text2">Get started by creating your first website</p>
            <button onClick={() => navigate('/create-website')} className="btn-primary mt-6">
              Create Your First Website
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0">
            {websites.map((website) =>
              isGenerating(website) || website.generation?.status === 'failed' ? (
                <GeneratingWebsiteCard
                  key={website._id}
                  website={website}
                  isLive={isConnected}
                  onDelete={() => handleDeleteWebsite(website._id)}
                />
              ) : (
                <div key={website._id} className="nova-card-hover min-w-0 overflow-hidden">
                  <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
                    <h3
                      className="font-display text-lg font-bold text-nova-text truncate min-w-0 flex-1"
                      title={website.businessName}
                    >
                      {website.businessName}
                    </h3>
                    <span className={`${website.hasPublishedVersion ? 'status-published' : 'status-draft'} shrink-0`}>
                      {website.hasPublishedVersion ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-sm text-nova-text3 mb-5">
                    Created: {new Date(website.createdAt).toLocaleDateString()}
                  </p>
                  <div className="space-y-2">
                    <button onClick={() => navigate(`/website/${website._id}`)} className="btn-primary w-full justify-center">
                      Edit Website
                    </button>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => window.open(getPreviewUrl(website.slug), '_blank')} className="btn-ghost justify-center text-sm">
                        Preview
                      </button>
                      <button
                        onClick={() => website.hasPublishedVersion && window.open(getLiveUrl(website.slug), '_blank')}
                        disabled={!website.hasPublishedVersion}
                        title={website.hasPublishedVersion ? undefined : 'Publish your website to view it live'}
                        className="btn-ghost justify-center text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        View Live
                      </button>
                    </div>
                    <button
                      onClick={() => handleShare(website)}
                      className="w-full text-nova-sky px-4 py-2 rounded-lg text-sm font-medium hover:bg-nova-sky/10 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342a4 4 0 010-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a4 4 0 105.367-1.317 4 4 0 00-5.367 1.317zm0 9.316a4 4 0 105.368 1.317 4 4 0 00-5.368-1.317z" />
                      </svg>
                      Share
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const GeneratingWebsiteCard: React.FC<{ website: Website; isLive: boolean; onDelete: () => void }> = ({
  website,
  isLive,
  onDelete,
}) => {
  const progress = website.generation || {};
  const failed = progress.status === 'failed';
  const total = progress.totalSections || 0;
  const rawCompleted = progress.completedSections || 0;
  const completed = total > 0 ? Math.min(rawCompleted, total) : rawCompleted;
  const percent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

  return (
    <div className={`nova-card min-w-0 overflow-hidden ${failed ? 'border-nova-red/40' : 'border-nova-sky/30'}`}>
      <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
        <h3 className="font-display text-lg font-bold text-nova-text truncate min-w-0 flex-1" title={website.businessName}>
          {website.businessName}
        </h3>
        {failed ? (
          <span className="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full bg-nova-red/15 text-nova-red border border-nova-red/20">
            Failed
          </span>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-nova-sky/15 text-nova-sky border border-nova-sky/20">
            <span
              className={`w-1.5 h-1.5 rounded-full ${isLive ? 'bg-nova-sky animate-pulse' : 'bg-nova-text3'}`}
              title={isLive ? 'Live updates connected' : 'Reconnecting…'}
            />
            Generating
          </span>
        )}
      </div>

      {failed ? (
        <p className="text-sm text-nova-text2 mb-5 break-words">
          {progress.error || 'Something went wrong while creating this website.'}
        </p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3 min-w-0">
            <div className="w-8 h-8 rounded-full border-2 border-nova-sky border-t-transparent animate-spin flex-shrink-0" />
            <p className="text-sm text-nova-text2 min-w-0 break-words">
              {progress.currentStep || 'Preparing your website...'}
            </p>
          </div>

          {total > 0 && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-nova-text3 mb-1.5">
                <span>{completed} of {total} sections created</span>
                <span>{percent}%</span>
              </div>
              <div className="w-full h-2 bg-nova-bg2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-nova-sky transition-all duration-700 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          )}

          <p className="text-sm text-nova-text3 mb-5">This usually takes around 5 minutes. Feel free to leave this page — we'll keep working in the background.</p>
        </>
      )}

      <div className="space-y-2">
        <button
          disabled
          className="btn-primary w-full justify-center opacity-50 cursor-not-allowed"
        >
          {failed ? 'Unavailable' : 'Edit Website'}
        </button>
        <button onClick={onDelete} className="w-full text-nova-red px-4 py-2 rounded-lg text-sm font-medium hover:bg-nova-red/10 transition-colors">
          Delete
        </button>
      </div>
    </div>
  );
};

export default DashboardPage;
